import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { toast } from "sonner";
import { useTimezone } from './contexts/TimezoneContext';
import ReferralModal from './components/ReferralModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RewardsPage = ({ user }) => {
  const navigate = useNavigate();
  const { formatTimestamp } = useTimezone();
  const [pointsData, setPointsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [referralLink, setReferralLink] = useState("");
  const [redeemAmount, setRedeemAmount] = useState("");
  const [redeemType, setRedeemType] = useState("uwyg_credit");
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);

  useEffect(() => {
    loadPointsData();
  }, []);

  const loadPointsData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/rewards/balance`, { withCredentials: true });
      setPointsData(response.data);
    } catch (error) {
      toast.error("Failed to load points data");
      console.error("Points data error:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateReferralLink = async () => {
    try {
      const response = await axios.post(`${API}/rewards/referral-link`, {}, { withCredentials: true });
      setReferralLink(response.data.referral_link);
      toast.success("Referral link generated! Share it to earn 200 points.");
    } catch (error) {
      toast.error("Failed to generate referral link");
      console.error("Referral link error:", error);
    }
  };

  const handleRedemption = async () => {
    const pointsToRedeem = parseInt(redeemAmount);
    
    if (!pointsToRedeem || pointsToRedeem <= 0) {
      toast.error("Please enter a valid points amount");
      return;
    }
    
    if (pointsToRedeem > pointsData?.points_balance) {
      toast.error("Insufficient points balance");
      return;
    }
    
    try {
      const redemptionData = {
        redemption_type: redeemType,
        points_to_redeem: pointsToRedeem,
        details: {
          requested_credit: (pointsToRedeem * 0.10).toFixed(2)
        }
      };
      
      const response = await axios.post(`${API}/rewards/redeem`, redemptionData, { withCredentials: true });
      
      toast.success(`Redemption request submitted for $${response.data.credit_amount}!`);
      setShowRedeemModal(false);
      setRedeemAmount("");
      loadPointsData(); // Refresh points data
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process redemption");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Use central MST formatter

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your rewards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Navigation with Logo */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/homepage')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
                alt="Digital Ink AI" 
                className="h-10 w-auto hover:opacity-80 transition-opacity"
              />
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/homepage')}
                className="text-slate-600 hover:text-slate-800"
              >
                ← Return to Homepage
              </Button>
              <span className="text-sm text-slate-600">{user?.first_name} {user?.last_name}</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            🎁 Your Rewards: Turn Activity into Publishing Perks
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Earn points for every paid Digital Ink AI service, then redeem them for discounts on your app subscription renewal, future paid services, or publishing services provided by{' '}
            <a 
              href="https://www.UWYGPublishing.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline font-medium"
            >
              Use What You Got Publishing
            </a>.
          </p>
        </div>

        {/* Points Balance Section */}
        {pointsData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <Card className="lg:col-span-2 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardHeader>
                <CardTitle className="text-2xl text-green-800">💎 Your Points Balance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center mb-6">
                  <div>
                    <div className="text-4xl font-bold text-green-600 mb-2">
                      {pointsData.points_balance.toLocaleString()}
                    </div>
                    <p className="text-green-700 font-medium">Current Points</p>
                    <p className="text-sm text-green-600">${pointsData.dollar_value} value</p>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-700 mb-2">
                      {pointsData.total_earned.toLocaleString()}
                    </div>
                    <p className="text-slate-600 font-medium">Total Earned</p>
                    <p className="text-sm text-slate-500">All time</p>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-700 mb-2">
                      {pointsData.total_redeemed.toLocaleString()}
                    </div>
                    <p className="text-slate-600 font-medium">Total Redeemed</p>
                    <p className="text-sm text-slate-500">${(pointsData.total_redeemed * 0.10).toFixed(2)} value</p>
                  </div>
                </div>
                
                {/* Points Breakdown by Type */}
                {pointsData.breakdown && (
                  <div className="border-t border-green-200 pt-4">
                    <p className="text-sm font-semibold text-green-800 mb-3">Points Earned by Activity:</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-blue-600">
                          {pointsData.breakdown.login_points.toLocaleString()}
                        </div>
                        <p className="text-xs text-slate-600">Daily Login Points</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-purple-600">
                          {pointsData.breakdown.service_points.toLocaleString()}
                        </div>
                        <p className="text-xs text-slate-600">Service Reward Points</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-orange-600">
                          {pointsData.breakdown.referral_points.toLocaleString()}
                        </div>
                        <p className="text-xs text-slate-600">Referral Points</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-800">🚀 Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  onClick={() => setShowRedeemModal(true)}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={pointsData.points_balance < 10}
                >
                  💰 Redeem Points
                </Button>
                <Button 
                  onClick={() => setShowReferralModal(true)}
                  variant="outline"
                  className="w-full"
                >
                  👥 Refer a Friend
                </Button>
                <Button 
                  onClick={() => navigate('/user_dashboard')}
                  variant="outline"
                  className="w-full"
                >
                  📈 View Activity
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* How to Earn Points */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-slate-900">🎯 How to Earn Points</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🔐</span>
                    <span className="font-medium">Daily Login</span>
                  </div>
                  <Badge className="bg-green-100 text-green-800">+10 points</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">📚</span>
                    <span className="font-medium">Paid Services</span>
                  </div>
                  <Badge className="bg-green-100 text-green-800">+10 / $ Spent</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">👥</span>
                    <span className="font-medium">Successful Referral</span>
                  </div>
                  <Badge className="bg-green-600 text-white">+200 points</Badge>
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Pro Tip:</strong> Share your referral link to earn the most points! 
                  When someone signs up using your link, you both benefit.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Redemption Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-slate-900">💳 Redemption Options</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border border-purple-200 bg-purple-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-purple-900">App Subscription Credit</h4>
                    <Badge className="bg-purple-600 text-white">$0.01/point</Badge>
                  </div>
                  <p className="text-sm text-purple-700 mb-3">
                    Apply points as credit toward your monthly or annual subscription
                  </p>
                  <p className="text-xs text-purple-600">Example: 500 points = $5 credit</p>
                </div>

                <div className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-orange-900">UWYG Publishing Credit</h4>
                    <Badge className="bg-orange-600 text-white">$0.01/point</Badge>
                  </div>
                  <p className="text-sm text-orange-700 mb-3">
                    Redeem for editing, cover design, printing, and publishing services
                  </p>
                  <a 
                    href="https://www.UWYGPublishing.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-orange-600 hover:text-orange-800 underline"
                  >
                    Visit UWYGPublishing.com →
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Referral Link Section */}
        {referralLink && (
          <Card className="mb-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
            <CardHeader>
              <CardTitle className="text-xl text-yellow-800">🔗 Your Referral Link</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3 items-center">
                <Input
                  value={referralLink}
                  readOnly
                  className="flex-1"
                />
                <Button onClick={() => copyToClipboard(referralLink)} className="bg-yellow-600 hover:bg-yellow-700">
                  📋 Copy Link
                </Button>
              </div>
              <p className="text-sm text-yellow-700 mt-3">
                Share this link with friends! You'll earn 200 points when they sign up and complete their first BCTG generation.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Points History */}
        {pointsData?.transactions && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-slate-900">📋 Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {pointsData.transactions.length > 0 ? (
                <div className="space-y-2">
                  {pointsData.transactions.slice(0, 10).map((transaction, index) => (
                    <div key={index} className="flex justify-between items-center p-3 border rounded-lg">
                      <div>
                        <div className="font-medium text-slate-900">
                          {transaction.description}
                        </div>
                        <div className="text-sm text-slate-500">
                          {transaction.created_at_display || formatTimestamp(transaction.created_at)} • {transaction.transaction_type.replace('_', ' ')}
                        </div>
                      </div>
                      <div className={`font-bold ${transaction.points >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {transaction.points >= 0 ? '+' : ''}{transaction.points} pts
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <p>No point transactions yet. Start earning points by using the app!</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Redemption Modal */}
      {showRedeemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">💰 Redeem Points</h3>
                <Button variant="outline" size="sm" onClick={() => setShowRedeemModal(false)}>✕</Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>Redemption Type</Label>
                  <select 
                    className="w-full p-2 border rounded mt-1"
                    value={redeemType}
                    onChange={(e) => setRedeemType(e.target.value)}
                  >
                    <option value="uwyg_credit">UWYG Publishing Credit</option>
                    <option value="app_upgrade">App Subscription Credit</option>
                  </select>
                </div>
                
                <div>
                  <Label>Points to Redeem</Label>
                  <Input
                    type="number"
                    placeholder="Enter points amount"
                    value={redeemAmount}
                    onChange={(e) => setRedeemAmount(e.target.value)}
                    className="mt-1"
                  />
                  {redeemAmount && (
                    <p className="text-sm text-slate-600 mt-1">
                      Credit Value: ${(parseInt(redeemAmount) * 0.10).toFixed(2)}
                    </p>
                  )}
                </div>

                <div className="bg-slate-50 p-3 rounded">
                  <p className="text-sm text-slate-600">
                    Available Balance: {pointsData?.points_balance || 0} points (${pointsData?.dollar_value || 0})
                  </p>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <Button 
                  onClick={handleRedemption}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={!redeemAmount || parseInt(redeemAmount) <= 0}
                >
                  Confirm Redemption
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowRedeemModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
                alt="Digital Ink AI" 
                className="h-8 w-auto"
              />
            </div>
            <p className="text-slate-400">
              © {new Date().getFullYear()} Digital Ink AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Referral Modal */}
      <ReferralModal 
        isOpen={showReferralModal}
        onClose={() => {
          setShowReferralModal(false);
          loadPointsData(); // Refresh points data after sending referral
        }}
      />
    </div>
  );
};

export default RewardsPage;